// The request the consumer builds, and the Exchange's limits on it. See Design
// > Exchange, "Send a request": 1 to 4 questions, each up to 25 words and 300
// characters, a bid above 0, `maxAnswers` 1 to 3 and at most 4 across the
// request, and at most 20 hub targets.
// The Exchange validates again; this is so the Console can say what's wrong
// before it sends.

import type { AskQuestion, AskRequest } from "./types.ts";

export const LIMITS = { questions: 4, words: 25, characters: 300, maxAnswers: 3, maxAnswersPerRequest: 4, hubTargets: 20 } as const;

export type Problem = { path: string; message: string };

const wordCount = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;
const positive = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n) && n > 0;

export function validateQuestion(q: AskQuestion, at: string): Problem[] {
  const problems: Problem[] = [];
  const text = typeof q.text === "string" ? q.text : "";
  if (text.trim() === "") problems.push({ path: `${at}.text`, message: "A question needs text." });
  else {
    if (wordCount(text) > LIMITS.words) problems.push({ path: `${at}.text`, message: `A question is at most ${LIMITS.words} words.` });
    if (text.length > LIMITS.characters) problems.push({ path: `${at}.text`, message: `A question is at most ${LIMITS.characters} characters.` });
  }
  if (q.maxCostPerAnswer === undefined) problems.push({ path: `${at}.maxCostPerAnswer`, message: "A question needs a bid." });
  else if (!positive(q.maxCostPerAnswer)) problems.push({ path: `${at}.maxCostPerAnswer`, message: "A bid is a number above 0." });
  if (q.maxAnswers !== undefined && !(Number.isInteger(q.maxAnswers) && q.maxAnswers >= 1 && q.maxAnswers <= LIMITS.maxAnswers))
    problems.push({ path: `${at}.maxAnswers`, message: `Max answers is 1 to ${LIMITS.maxAnswers}.` });
  if (q.hubs !== undefined) {
    if (!Array.isArray(q.hubs) || q.hubs.some((h) => typeof h !== "string" || h === "")) problems.push({ path: `${at}.hubs`, message: "Hub targets are names or globs." });
    else if (q.hubs.length > LIMITS.hubTargets) problems.push({ path: `${at}.hubs`, message: `At most ${LIMITS.hubTargets} hub targets.` });
  }
  return problems;
}

export function validateRequest(req: AskRequest): Problem[] {
  const questions = req.questions;
  if (!Array.isArray(questions) || questions.length === 0) return [{ path: "questions", message: "A request needs at least one question." }];
  const problems: Problem[] = [];
  if (questions.length > LIMITS.questions) problems.push({ path: "questions", message: `A request has at most ${LIMITS.questions} questions.` });
  questions.forEach((q, i) => problems.push(...validateQuestion(q ?? {}, `questions[${i}]`)));
  const total = questions.reduce((n, q) => n + (q?.maxAnswers ?? 1), 0);
  if (questions.length <= LIMITS.questions && total > LIMITS.maxAnswersPerRequest) problems.push({ path: "questions", message: `Max answers add up to ${total}; a request's total is at most ${LIMITS.maxAnswersPerRequest}.` });
  if (req.maxTotalCost !== undefined && !positive(req.maxTotalCost)) problems.push({ path: "maxTotalCost", message: "A total cost limit is a number above 0." });
  return problems;
}

export type Parsed = { request: AskRequest | null; error: string | null };

/** Reads the JSON the consumer typed. A syntax error keeps the text and says why. */
export function parseRequestText(text: string): Parsed {
  try {
    const value: unknown = JSON.parse(text);
    if (value === null || typeof value !== "object" || Array.isArray(value)) return { request: null, error: "A request is a JSON object." };
    return { request: value, error: null };
  } catch (e) {
    return { request: null, error: e instanceof Error ? e.message : "Not valid JSON." };
  }
}

export const formatRequest = (req: AskRequest): string => JSON.stringify(req, null, 2);
export const EMPTY_REQUEST_TEXT = "{}";
export const isEmptyRequest = (req: AskRequest | null): boolean => req !== null && Object.keys(req).length === 0;

/** The first question, which the Playground's form edits. */
export const firstQuestion = (req: AskRequest | null): AskQuestion => req?.questions?.[0] ?? {};

/** Changes the first question, keeping the rest of the request. `cleared` names a field to remove, as an emptied form field leaves it. */
export function withFirstQuestion(req: AskRequest | null, change: Partial<AskQuestion>, cleared?: keyof AskQuestion): AskRequest {
  const base: AskRequest = req ?? {};
  const [first, ...rest] = base.questions?.length ? base.questions : [{ id: "q1" }];
  const merged: AskQuestion = { ...first, ...change };
  if (cleared === undefined) return { ...base, questions: [merged, ...rest] };
  const { [cleared]: _cleared, ...kept } = merged;
  return { ...base, questions: [kept, ...rest] };
}

/** Sets the request's total cost limit, or removes it when `cost` is undefined, as an emptied form field leaves it. */
export function withMaxTotalCost(req: AskRequest | null, cost: number | undefined): AskRequest {
  const { maxTotalCost: _cleared, ...kept } = req ?? {};
  return cost === undefined ? kept : { ...kept, maxTotalCost: cost };
}

// The Exchange refuses a refund reason over this many characters. See Design >
// Exchange, "Reject an answer".
export const MAX_REASON_CHARS = 500;
